import React, { useState } from 'react';
import { Shield, CheckCircle, Award, Zap, Target, TrendingUp, Users, Clock, Truck, FileCheck } from 'lucide-react';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';
import bg_img from '../assets/quality&services_bg_img.jpg';
import Lab_1 from '../assets/quality/lab-1.jpg';
import Lab_2 from '../assets/quality/lab-2.jpg';
import Lab_3 from '../assets/quality/lab-3.png';
import Lab_4 from '../assets/quality/lab-4.jpg';
import Service_1 from '../assets/services/services-1.jpeg';
import Service_2 from '../assets/services/services-2.jpeg';
import Service_3 from '../assets/services/services-3.jpeg';
import Service_4 from '../assets/services/services-4.jpeg';

const QualityServices = () => {
    const qualityImages = [
        Lab_1,
        Lab_2,
        Lab_3,
        Lab_4
    ];
    const servicesImages = [
        Service_1,
        Service_2,
        Service_3,
        Service_4
    ];

    const [qualityCurrentIndex, setQualityCurrentIndex] = useState(0);
    const [servicesCurrentIndex, setServicesCurrentIndex] = useState(0);
    const [qualityPaused, setQualityPaused] = useState(false);
    const [servicesPaused, setServicesPaused] = useState(false);

    const handleQualityPrev = () => setQualityCurrentIndex((prev) => (prev === 0 ? qualityImages.length - 1 : prev - 1));
    const handleQualityNext = () => setQualityCurrentIndex((prev) => (prev === qualityImages.length - 1 ? 0 : prev + 1));
    const handleServicesPrev = () => setServicesCurrentIndex((prev) => (prev === 0 ? servicesImages.length - 1 : prev - 1));
    const handleServicesNext = () => setServicesCurrentIndex((prev) => (prev === servicesImages.length - 1 ? 0 : prev + 1));

    // Autoplay for quality slider
    React.useEffect(() => {
        if (qualityPaused || qualityImages.length <= 1) return;
        const id = setInterval(() => {
            setQualityCurrentIndex((prev) => (prev === qualityImages.length - 1 ? 0 : prev + 1));
        }, 2000);
        return () => clearInterval(id);
    }, [qualityPaused, qualityImages.length]);

    // Autoplay for services slider
    React.useEffect(() => {
        if (servicesPaused || servicesImages.length <= 1) return;
        const id = setInterval(() => {
            setServicesCurrentIndex((prev) => (prev === servicesImages.length - 1 ? 0 : prev + 1));
        }, 2000);
        return () => clearInterval(id);
    }, [servicesPaused, servicesImages.length]);

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />
            {/* Hero Section */}
            <section className="relative h-[50vh] sm:h-[60vh] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img
                        src={bg_img}
                        alt="Quality Control"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/60 to-transparent"></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-slate-900/40"></div>
                </div>
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 animate-fadeInUp">
                        Quality & <span className="text-orange-500">Services</span>
                    </h1>
                    <p className="text-base sm:text-lg lg:text-xl text-gray-300 mb-6 sm:mb-8 max-w-xl sm:max-w-2xl lg:max-w-3xl mx-auto animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
                        Excellence in Every Product, Service in Every Interaction
                    </p>
                </div>
            </section>


            {/* Stats Section */}
            <section className="py-6 sm:py-8 bg-gradient-to-r from-yellow-500 to-orange-500">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
                        {[
                            { icon: Shield, number: '100%', label: 'Quality Tested' },
                            { icon: TrendingUp, number: '99%', label: 'Customer Satisfaction' },
                            { icon: Clock, number: '98%', label: 'On-Time Delivery' },
                            { icon: Award, number: '30+', label: 'Years Experience' }
                        ].map((stat, index) => (
                            <div key={index} className="text-center transform hover:scale-110 transition-transform duration-300">
                                <stat.icon className="w-8 h-8 sm:w-10 sm:h-10 text-white mx-auto mb-3 sm:mb-4" />
                                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-1 sm:mb-2">{stat.number}</div>
                                <div className="text-sm sm:text-base lg:text-white/90 font-medium">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            

            {/* Our Quality Section */}
            <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        {/* Left Side - Content */}
                        <div>
                            <div className="inline-block mb-4">
                                <span className="text-orange-600 font-semibold text-sm tracking-wider uppercase">Excellence First</span>
                            </div>
                            <h2 className="text-4xl font-bold text-gray-900 mb-6">
                                OUR <span className="text-orange-600">QUALITY</span>
                            </h2>
                            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                                Quality is the prime concern of our company since the inception and for maintaining this; we offer only splendid range of products to our prestigious clients. All our offered products are precisely developed under the direction of our skilled professionals in conformity with the international norms.
                            </p>
                            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                                For rigorous inspection of entire products, we have hired a team of quality controllers. Our skilled quality personnel make use of latest technologies & machines in the checking procedure in order to eliminate faults in offered range. We have installed renowned equipments and machineries to create quality products. We are also with sophisticated & state of art technology laboratory equipments at our testing laboratory.
                            </p>
                            <div className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white p-6 rounded-xl shadow-lg mb-6">
                                <p className="text-xl font-bold text-center">
                                    "NO COMPROMISE IN QUALITY"
                                </p>
                            </div>
                        </div>

                        {/* Right Side - Image Slider */}
                        <div className="relative order-2 md:order-2">
                            <div
                                className="relative overflow-hidden rounded-2xl shadow-2xl h-[300px] sm:h-[400px] w-full sm:w-96 mx-auto sm:ml-20"
                                onMouseEnter={() => setQualityPaused(true)}
                                onMouseLeave={() => setQualityPaused(false)}
                            >
                                <img
                                    src={qualityImages[qualityCurrentIndex]}
                                    alt="Quality Control Testing"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="absolute -bottom-6 -left-6 bg-gradient-to-r from-yellow-500 to-orange-500 text-white p-5 rounded-xl shadow-xl">
                                <div className="text-2xl font-bold">ISO Certified</div>
                                <div className="text-sm">Quality Assured</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Our Services Section */}
            <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        {/* Left Side - Image Slider */}
                        <div className="relative order-1 md:order-1">
                            <div
                                className="relative overflow-hidden rounded-2xl shadow-2xl h-[300px] sm:h-[400px] w-full mx-auto"
                                onMouseEnter={() => setServicesPaused(true)}
                                onMouseLeave={() => setServicesPaused(false)}
                            >
                                <img
                                    src={servicesImages[servicesCurrentIndex]}
                                    alt="Logistics and Delivery"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="absolute -bottom-6 -right-6 bg-gradient-to-r from-yellow-500 to-orange-500 text-white p-5 rounded-xl shadow-xl">
                                <div className="text-2xl font-bold">24/7 Support</div>
                                <div className="text-sm">Always Available</div>
                            </div>
                        </div>

                        {/* Right Side - Content */}
                        <div className="order-2 md:order-2">
                            <div className="inline-block mb-4">
                                <span className="text-orange-600 font-semibold text-sm tracking-wider uppercase">Customer First</span>
                            </div>
                            <h2 className="text-4xl font-bold text-gray-900 mb-6">
                                OUR <span className="text-orange-600">SERVICES</span>
                            </h2>
                            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                                We at JANTA Group understand it well that in business, good service is as essential as good quality. As we have a prudent schedule and delivery system, we are able to manage even huge quantity orders smoothly and on time.
                            </p>
                            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                                On account of excellent connectivity of our warehousing facility with various transportation networks, we have been capable to offer on-time delivery of products at the customers' preferred destination within the committed time frame. In addition to this, our entire work-force also brushed up their skills by the training sessions offered at our firm at regular intervals.
                            </p>
                            <div className="flex items-center gap-3 text-orange-600 font-semibold bg-orange-50 p-4 rounded-lg">
                                <CheckCircle className="w-6 h-6" />
                                <span>Committed to Excellence in Service Delivery</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Choose Our Quality & Services */}
            <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">Why Choose <span className="text-orange-500">JANTA Group</span></h2>
                        <p className="text-base sm:text-lg lg:text-xl text-gray-300">Commitment to Excellence at Every Step</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: Shield,
                                title: 'Quality Assurance',
                                description: 'State-of-the-art testing laboratory with advanced equipment ensuring every product meets international standards.'
                            },
                            {
                                icon: Users,
                                title: 'Skilled Team',
                                description: 'Expert professionals with regular training sessions ensuring cutting-edge knowledge and technical expertise.'
                            },
                            {
                                icon: Truck,
                                title: 'Reliable Delivery',
                                description: 'Extensive logistics network and efficient warehousing ensuring timely delivery to any destination.'
                            }
                        ].map((item, index) => (
                            <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 sm:p-8 hover:bg-white/15 transition-all">
                                <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mb-6">
                                    <item.icon className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="text-2xl font-bold text-white mb-4">{item.title}</h3>
                                <p className="text-white leading-relaxed">{item.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <style jsx>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fadeInUp {
                    animation: fadeInUp 0.8s ease-out forwards;
                    opacity: 0;
                }
            `}</style>
            <Footer />
        </div>
    );
};

export default QualityServices;